import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  BoxExtendedProps,
  costFormater,
  Preloader,
  Text,
  useResponsive,
  WarningIcon,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { ValuesMap } from '@/common/types';
import { Modal } from '@/components';
import { FundPayload } from '@/modules/investments/SelfDirected/SelfDirected.types';

import { PERCENTS_LIQUIDATION_LIMIT } from '../Funds.constants';

import ConfirmModalItem from './ConfirmModalItem';
import ConfirmModalListHeader from './ConfirmModalListHeader';

interface WarningBlockProps extends BoxExtendedProps {
  children: React.ReactNode;
}
const WarningBlock: React.FC<WarningBlockProps> = ({ children, ...props }) => {
  const { isMobile } = useResponsive();
  return (
    <Box
      direction="row"
      align="center"
      background="warningContainer"
      border={{ color: 'warningBorder', size: '1px' }}
      round="container2Round"
      pad={
        isMobile
          ? { vertical: 'spacing12', horizontal: 'spacing16' }
          : { vertical: 'spacing16', horizontal: 'spacing24' }
      }
      {...props}
    >
      <div>
        <Box>
          <WarningIcon />
        </Box>
      </div>
      <Text margin={{ left: 'xs' }}>{children}</Text>
    </Box>
  );
};
interface Props {
  list: FundPayload[];
  isLoading?: boolean;
  values: ValuesMap<number>;
  liquidationValues: ValuesMap<boolean>;
  visible: boolean;
  onSetVisible: (value: boolean) => void;
  onSubmit: () => void;
}
const ConfirmModal: React.FC<Props> = ({
  isLoading,
  visible,
  onSetVisible,
  list,
  values,
  onSubmit,
  liquidationValues,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const getCurrentValue = useCallback(
    (item: FundPayload) => {
      const currentValue = values[item.id] || 0;
      if (liquidationValues[item.id]) {
        return item.amount || currentValue || 0;
      }
      const percents = (item.amount || 0) / currentValue;
      if (_.isNaN(percents)) {
        return currentValue || item.amount || 0;
      }
      if (percents >= PERCENTS_LIQUIDATION_LIMIT) {
        return currentValue || item.amount || 0;
      }
      return currentValue;
    },
    [liquidationValues, values]
  );

  const filteredList = useMemo(() => {
    const existingIds = Object.keys(values).map((item) =>
      liquidationValues[item] || values[item] > 0 ? item : null
    );
    return list.filter((item) => existingIds.includes(item.id));
  }, [list, values, liquidationValues]);

  const totalAmount = useMemo(() => {
    return filteredList.reduce((result, item) => {
      return result + getCurrentValue(item);
    }, 0);
  }, [getCurrentValue, filteredList]);

  return (
    <Modal visible={visible} onSetVisible={onSetVisible} wrapperStyle={{ zIndex: 1000 }}>
      <Box direction="column" pad={{ bottom: isMobile ? 'spacing16' : 'spacing24' }}>
        <Text size="2xl" textAlign="center" weight={700}>
          {t('Confirm')}
        </Text>
      </Box>
      <Box
        direction="column"
        background="module"
        pad={isMobile ? 'spacing8' : 'spacing24'}
        round="moduleRound"
      >
        {!isMobile && <ConfirmModalListHeader />}
        <Box direction="column">
          {filteredList.map((item) => (
            <ConfirmModalItem
              testId={`confirm-modal-item_${item.id}`}
              value={getCurrentValue(item)}
              key={item.id}
              {...item}
            />
          ))}
        </Box>
        <Box
          direction="row"
          pad={isMobile ? 'spacing12' : { vertical: '6px', horizontal: 'spacing24' }}
          background="border1"
          margin={{ top: 'spacing8', bottom: 'spacing24' }}
          round="container2Round"
          justify="between"
        >
          <Text size="medium" weight={700} style={{ flex: 2 }}>
            {t('Total')}
          </Text>
          <Box width="56px"></Box>
          <Box direction="row" style={isMobile ? {} : { flex: 2 }}>
            <Box>
              <Text data-testid="confirm-modal_total-amount" weight={700} textAlign="end">
                {costFormater(totalAmount, {
                  isPrecision: true,
                })}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box
          background="canvas"
          border={{ color: 'border2', size: 'small' }}
          round="container1Round"
          pad="s"
          elevation="default"
          style={{ position: 'relative' }}
        >
          <Box
            height="2px"
            round="small"
            background="warning"
            style={{ position: 'absolute', top: '-0.5px', left: '12px', right: '12px' }}
          />
          <WarningBlock>
            {t(
              'Sells can take up to 2 to 3 business days to be reflected in your account. Any automatic investments will be paused.'
            )}
          </WarningBlock>

          <WarningBlock margin={{ top: '10px' }}>
            {t('New investment purchases will be temporarily restricted until the sell order is settled.')}
          </WarningBlock>
        </Box>
      </Box>

      <Box direction="row" gap="s" justify="end" margin={{ top: isMobile ? 'spacing12' : 'spacing24' }}>
        <Box fill={isMobile}>
          <AppButton
            fill={isMobile}
            buttonType="secondary"
            width="140px"
            testId="confirm-modal_cancel"
            onClick={handleClose}
          >
            {t('Back')}
          </AppButton>
        </Box>
        <Box fill={isMobile}>
          <AppButton
            fill={isMobile}
            testId="confirm-modal_submit"
            disabled={isLoading}
            width="140px"
            onClick={onSubmit}
          >
            {isLoading ? <Preloader color="white" /> : t('Confirm')}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmModal;
